// Şoför/rehber uygulaması sözleşmesi (D01–D34): API girdileri Zod, yanıtlar tip. İstemci ve API ortak kullanır.
import { z } from 'zod';
import {
  BOARDING_METHODS,
  BOARDING_TYPES,
  DRIVER_DOCUMENTS,
  INCIDENT_TYPES,
  type BoardingMethod,
  type BoardingType,
  type DocumentStatus,
  type DocumentType,
  type IncidentType,
  type PassengerKind,
  type TripStatus,
} from '../enums';

// ───────── girdiler ─────────

export const BoardingEventInput = z.object({
  clientEventId: z.string().min(8).max(80),
  tripId: z.string(),
  passengerId: z.string(),
  stopId: z.string().optional(),
  type: z.enum(BOARDING_TYPES),
  method: z.enum(BOARDING_METHODS),
  occurredAt: z.string(),
  pickupPersonId: z.string().optional(),
  /** QR/PIN ile binişte ham kod — çevrimdışı okutulduysa eşitlemede sunucu doğrular */
  token: z.string().max(200).optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  note: z.string().max(280).optional(),
});
export type BoardingEventInput = z.infer<typeof BoardingEventInput>;

/** Çevrimdışı başlatılan seferde gerçek başlama saati gönderilir (en fazla 6 saat geriye). */
export const StartTripInput = z.object({
  check: z.record(z.string(), z.boolean()).optional(),
  at: z.string().optional(),
});
export type StartTripInput = z.infer<typeof StartTripInput>;

export const EndTripInput = z.object({ at: z.string().optional() });

export const VEHICLE_CHECK_ITEMS = ['fuel', 'tires', 'cleanliness'] as const;
export type VehicleCheckItem = (typeof VEHICLE_CHECK_ITEMS)[number];
export const VEHICLE_CHECK_LABELS: Record<VehicleCheckItem, string> = {
  fuel: 'Yakıt yeterli',
  tires: 'Lastikler sağlam',
  cleanliness: 'Araç temiz',
};

export const VehicleCheckInput = z.object({
  items: z.record(z.enum(VEHICLE_CHECK_ITEMS), z.boolean()),
  problem: z.string().max(500).optional(),
});
export type VehicleCheckInput = z.infer<typeof VehicleCheckInput>;

export const ArriveInput = z.object({ at: z.string().optional() });

export const BoardingVerifyInput = z.object({ tripId: z.string(), code: z.string().min(4).max(200) });
export type BoardingVerifyInput = z.infer<typeof BoardingVerifyInput>;

export const IncidentInput = z.object({
  type: z.enum(INCIDENT_TYPES),
  canContinue: z.boolean().default(true),
  note: z.string().max(500).optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  at: z.string().optional(),
  /** çevrimdışı kuyruk tekrar gönderiminde çift kayıt olmasın */
  clientId: z.string().min(8).max(80).optional(),
});
export type IncidentInput = z.infer<typeof IncidentInput>;

export const DriverProfileInput = z.object({
  firstName: z.string().trim().min(2, 'Ad en az 2 harf olmalı').max(60),
  lastName: z.string().trim().min(2, 'Soyad en az 2 harf olmalı').max(60),
  email: z.string().email('Geçerli bir e-posta girin').max(120).or(z.literal('')).optional(),
  licenseClass: z.string().max(10).optional(),
  photoFileId: z.string().optional(),
});
export type DriverProfileInput = z.infer<typeof DriverProfileInput>;

export const DriverDocumentInput = z.object({
  type: z.enum(DRIVER_DOCUMENTS as [DocumentType, ...DocumentType[]]),
  fileId: z.string(),
  number: z.string().max(40).optional(),
  validUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı')
    .optional(),
});
export type DriverDocumentInput = z.infer<typeof DriverDocumentInput>;

export const MaskedCallInput = z.object({ passengerId: z.string().optional() });

// ───────── yanıtlar ─────────

export interface DriverTodayTrip {
  id: string;
  status: TripStatus;
  direction: 'OUTBOUND' | 'INBOUND';
  plannedStartAt: string;
  plannedEndAt: string;
  routeCode: string;
  routeName: string;
  institution: string;
  plate: string;
  vehicle: string;
  stops: number;
  passengers: number;
  absent: number;
  /** rehber olarak atandığı sefer */
  asGuide?: boolean;
}

export interface DriverToday {
  date: string;
  trips: DriverTodayTrip[];
  /** belge uyarısı (süresi 30 gün içinde dolan en yakın belge) */
  documentWarning: { id: string; type: DocumentType; label: string; daysLeft: number; status: DocumentStatus } | null;
  unreadAnnouncements: number;
}

export interface RunPassenger {
  id: string;
  name: string;
  kind: PassengerKind;
  className: string | null;
  absent: boolean;
  ready: boolean;
  status: BoardingType | null;
}

export interface RunStop {
  id: string;
  order: number;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  along: number;
  kind: string;
  plannedAt: string;
  etaAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  passengers: RunPassenger[];
}

export interface DriverRun {
  id: string;
  status: TripStatus;
  direction: 'OUTBOUND' | 'INBOUND';
  routeCode: string;
  routeName: string;
  institution: string;
  plate: string;
  vehicle: string;
  capacity: number;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt: string | null;
  delayMin: number;
  polyline: string;
  distanceM: number;
  guide: string | null;
  vehicleChecked: boolean;
  /** sunucunun aktif biniş yöntemleri (firma ayarı) */
  boardingMethods: BoardingMethod[];
  /** durakta yolcu için bekleme (dk) — firma/kurum sefer kuralı */
  waitMin: number;
  /** varış ekranı yarıçapı (m) — firma geofence ayarı */
  arrivalRadiusM: number;
  stops: RunStop[];
}

export interface BoardingVerifyResult {
  ok: boolean;
  method: 'QR' | 'PIN';
  passenger: {
    id: string;
    name: string;
    kind: PassengerKind;
    stopId: string | null;
    stopName: string | null;
    alreadyBoarded: boolean;
  } | null;
  reason?: 'INVALID' | 'NOT_ON_TRIP' | 'EXPIRED';
}

export interface TripSummary {
  id: string;
  routeCode: string;
  routeName: string;
  plate: string;
  status: TripStatus;
  startedAt: string | null;
  endedAt: string | null;
  durationMin: number | null;
  distanceKm: number | null;
  boarded: number;
  noShow: number;
  absent: number;
  assigned: number;
  delayMin: number;
  onTimeStopsPct: number | null;
  incidents: number;
}

export interface DriverTripListItem {
  id: string;
  date: string;
  status: TripStatus;
  direction: 'OUTBOUND' | 'INBOUND';
  plannedStartAt: string;
  routeCode: string;
  routeName: string;
  plate: string;
  boarded: number | null;
  delayMin: number;
  distanceKm: number | null;
}

export interface DriverIncidentResult {
  id: string;
  alertRaised: boolean;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
}

export interface ReplacementInfo {
  tripId: string;
  plate: string;
  driver: string;
  etaMin: number | null;
  assignedAt: string;
  routeCode: string;
}

export interface DriverDocument {
  id: string;
  type: DocumentType;
  label: string;
  number: string | null;
  validUntil: string | null;
  status: DocumentStatus;
  daysLeft: number | null;
  fileUrl: string | null;
  createdAt: string;
}

export interface DriverProfile {
  id: string;
  firstName: string;
  lastName: string;
  phoneMasked: string;
  email: string | null;
  employeeNo: string | null;
  licenseClass: string | null;
  hiredAt: string | null;
  company: string;
  photoUrl: string | null;
  role: 'DRIVER' | 'GUIDE';
  rating: number | null;
  tripsThisMonth: number;
  validDocuments: string[];
  plate: string | null;
  /** D03 ilk kurulum tamamlandı mı */
  setupDone: boolean;
}

export interface EarningsLine {
  label: string;
  amount: number;
  kind: 'BASE' | 'BONUS' | 'DEDUCTION' | 'EXTRA';
}

export interface EarningsPeriod {
  id: string;
  period: string;
  label: string;
  tripCount: number;
  km: number;
  baseAmount: number;
  bonus: number;
  deduction: number;
  total: number;
  status: 'DRAFT' | 'APPROVED' | 'PAID';
  paidAt: string | null;
  lines: EarningsLine[];
}

export interface AnnouncementItem {
  id: string;
  title: string;
  body: string;
  author: string;
  sentAt: string;
  read: boolean;
}

export const INCIDENT_SEVERITY: Record<IncidentType, 'INFO' | 'WARNING' | 'CRITICAL'> = {
  TRAFFIC: 'INFO',
  ROAD_CLOSED: 'WARNING',
  PASSENGER: 'WARNING',
  OTHER: 'WARNING',
  ACCIDENT: 'CRITICAL',
  BREAKDOWN: 'CRITICAL',
  MEDICAL: 'CRITICAL',
};
